// The UI half of the replay presentation (§11, §14.2), shared by the Instant Replay (Tally), Play of
// the Match (Awards) and BONK CAM (HUD). The Renderer's VHS layer (render/replay/VhsOverlay.ts)
// owns the camcorder look: scanlines and tracking in the shader, the corner tag, the tape counter,
// the title card and BONK CAM's "BONKED BY". This layer owns what the player acts on and reads:
//  - a full-screen tap/click target and "[Enter] to skip" (bottom-right; the tape counter is bottom-left),
//  - the collective skip state of the shared replays (§9.5): "SKIPPED ✓ 1/3" from `room.skip`,
//  - the billing of the Instant Replay ("FINAL BONK a ➜ b") and Play of the Match ("STARRING a").
import { NO_SLOT } from '@shared/constants';
import type { ReplayKind } from '../../game/FrameView';
import type { UiDeps } from '../deps';
import { actionCap } from '../glyphView';
import { identityBadge } from '../icons';
import { el, replayClass, setHidden, setText, toggleClass } from '../ui';

export interface ReplayOverlay { el: HTMLElement; update(): boolean; active(): boolean; skipped(): boolean; skip(): void }

/** Connected humans in the room and how many of them pressed Skip (`room.skip`, WP3). */
export function skipTally(deps: UiDeps): { humans: number; skipped: number } {
  const dto = deps.room.dto;
  if (!dto) return { humans: 0, skipped: 0 };
  const skip = dto.skip ?? [];
  let humans = 0;
  let skipped = 0;
  for (const p of dto.players) {
    if (!p || p.isBot || !p.connected) continue;
    humans++;
    if (skip.includes(p.slot)) skipped++;
  }
  return { humans, skipped };
}

export function replayOverlay(deps: UiDeps, kinds: readonly ReplayKind[] = ['instant', 'potm']): ReplayOverlay {
  const credit = el('div', { class: 'vhs-killer' });
  const skipText = el('span');
  const skipCap = el('span', { class: 'vhs-cap' });
  const skip = el('div', { class: 'vhs-skip' }, skipCap, skipText);
  const root = el('div', { class: 'vhs', attrs: { 'aria-live': 'polite' }, on: { click: () => api.skip() } }, credit, skip);
  root.hidden = true;
  let active = false;
  let kind = '';
  let didSkip = false;
  let capDevice = '';
  let skipKey = '';

  const api: ReplayOverlay = {
    el: root,
    active: () => active,
    skipped: () => didSkip,
    skip() {
      if (!active || didSkip) return;
      didSkip = true;
      deps.services.playUi('click');
      deps.actions.skipReplay();
    },
    update() {
      const r = deps.services.replay();
      active = r !== null && kinds.includes(r.kind);
      setHidden(root, !active);
      if (!r || !active) {
        kind = '';
        didSkip = false;
        return false;
      }
      if (r.kind !== kind) {
        kind = r.kind;
        didSkip = false;
        skipKey = '';
        root.dataset.kind = r.kind;
        toggleClass(root, 'potm', r.kind === 'potm');
        toggleClass(root, 'bonkcam', r.kind === 'bonkcam');
        replayClass(root, 'vhs-in');
        const live = deps.view();
        const cb = deps.settings().accessibility.colorblind;
        const bean = (slot: number) => (slot !== NO_SLOT && live.beans[slot]?.active ? live.beans[slot] : null);
        const k = bean(r.killerSlot);
        const v = bean(r.focusSlot);
        const star = k ?? v;
        const who = (b: NonNullable<typeof k>) => [identityBadge(b.identity, cb), el('b', { text: b.name.toUpperCase() })];
        // The Instant Replay and Play of the Match bill their stars (BONK CAM's "BONKED BY" is the
        // Renderer's).
        credit.replaceChildren(...(r.kind === 'potm' && star ? [el('span', { text: 'STARRING' }), ...who(star)]
          : r.kind === 'instant' && k && v && k !== v ? [el('span', { text: 'FINAL BONK' }), ...who(k), el('i', { class: 'vhs-arrow', text: '➜' }), ...who(v)]
            : r.kind === 'instant' && v ? [el('span', { text: 'FINAL FALL' }), ...who(v)]
              : []));
        setHidden(credit, credit.childElementCount === 0);
      }
      const dev = deps.services.input.device;
      if (dev !== capDevice) {
        capDevice = dev;
        skipCap.replaceChildren(...(dev === 'touch' ? [] : [actionCap(deps, 'confirm')]));
      }
      // The Instant Replay's skip is collective: show how many of the room's humans pressed it.
      const shared = r.kind === 'instant';
      const t = shared ? skipTally(deps) : { humans: 1, skipped: 0 };
      const key = `${dev}|${didSkip}|${t.humans}|${t.skipped}`;
      if (key !== skipKey) {
        skipKey = key;
        const count = shared && t.humans > 1 ? ` ${Math.max(t.skipped, didSkip ? 1 : 0)}/${t.humans}` : '';
        setText(skipText, didSkip ? (shared && t.humans > 1 ? `SKIPPED ✓${count} — waiting for the others` : 'Skipping…')
          : `${dev === 'touch' ? 'Tap to skip' : 'to skip'}${count && t.skipped > 0 ? ` •${count} skipped` : ''}`);
        toggleClass(skip, 'done', didSkip);
      }
      return true;
    },
  };
  return api;
}
